import { useQuery } from "@tanstack/react-query";
import SubscriptionRepository from "@/repositories/subscription.repo";
import useAuth from "./auth.hook";

/**
 * Plan entitlements for the current business (from /subscriptions/me → plans.config features).
 * `hasFeature` returns true for super-admins and for features included in the active plan.
 */
const usePlanFeatures = () => {
  const { isSuperAdmin } = useAuth();
  const repository = new SubscriptionRepository();

  const { data } = useQuery({
    queryKey: ["plan-me"],
    queryFn: () => repository.me(),
    enabled: !isSuperAdmin,
    refetchOnWindowFocus: false,
    staleTime: 5 * 60 * 1000,
  });

  const features = (data?.features ?? {}) as Record<string, boolean>;
  const hasFeature = (key?: string) => isSuperAdmin || !key || !!features[key];

  // Actual resource counts for this business — used to surface only the menus
  // the business has genuinely set up (e.g. hide hotel ops for a travel-only business).
  const usage = (data?.usage ?? {}) as Partial<Record<string, number>>;

  // Super-admin per-business overrides: UI module keys hidden from this business.
  const hiddenModules = (data?.hidden_modules ?? []) as string[];
  // A module is visible unless it's in the hidden set. Super-admins always see everything.
  const isModuleVisible = (key?: string) => isSuperAdmin || !key || !hiddenModules.includes(key);

  // Expiry status of the business's paid subscription (for the dashboard warning banner).
  const subscription = data?.subscription;

  return { features, hasFeature, usage, hiddenModules, isModuleVisible, subscription };
};

export default usePlanFeatures;
